import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BrandSymbol,
  Button,
  Container,
  Divider,
  Group,
  Icon,
  Link,
  Loader,
  SectionBannerCloseButton,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  SectionBannerTitle,
  Text,
  useBreakpointValue,
} from '@melio/penny';

import { getGrantInteraction, getSession, grantScopes, logout, GrantInteraction, ScopeWithDescription } from '../../api/auth';
import { useAnalytics } from '../../analytics';
import { Logger } from '../../monitoring';
import { sanitizeRedirectParam } from '../../utils/sanitizeRedirectUrl';

const PAGE_NAME = 'consent';
const FLOW = 'consent';

type LoadState = { type: 'loading' } | { type: 'ready'; email: string; interaction: GrantInteraction } | { type: 'invalid' };

// The dashed connector line between the app logo and the Melio mark; matches
// the design's 61.56x1 line (stroke #C3CAD5, 4-4 dashes), which Penny has no
// primitive for.
const ConnectorLine = () => (
  <svg width="61.56" height="1" viewBox="0 0 61.56 1" fill="none" aria-hidden>
    <line x1="0" y1="0.5" x2="61.56" y2="0.5" stroke="#C3CAD5" strokeDasharray="4 4" />
  </svg>
);

const ScopeList = ({ scopes, testId }: { scopes: ScopeWithDescription[]; testId: string }) => (
  <Group variant="vertical" spacing="s" data-testid={testId}>
    {scopes.map(({ scope, description }) => (
      <Group key={scope} variant="horizontal" spacing="xxs" alignItems="center" data-testid={`${testId}-${scope}`}>
        <Text as="span" color="semantic.text.secondary" aria-hidden>
          <Icon type="checked-mini" size="small" color="inherit" />
        </Text>
        <Text textStyle="body3" color="semantic.text.secondary">
          {description}
        </Text>
      </Group>
    ))}
  </Group>
);

export const GrantPage = () => {
  const isMobile = useBreakpointValue({ xs: true, s: false });
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();

  const interactionId = searchParams.get('interactionId') ?? '';
  // The re-entrant authorize URL, delivered on the /grant URL by
  // /oauth/authorize itself. Same-origin enforced before any navigation.
  const safeRedirect = sanitizeRedirectParam(searchParams.get('redirect') ?? '') ?? '';

  const [loadState, setLoadState] = useState<LoadState>({ type: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);

  document.title = 'Authorize access';

  // Resolve everything the page needs. /oauth/authorize is the flow's
  // orchestrator: with no valid session this page never routes to sign-in
  // itself - it navigates back to the authorize URL it received on the /grant
  // URL, and authorize handles login and mints a fresh interaction.
  const resolveLoadState = async (): Promise<LoadState | { type: 'navigate'; url: string }> => {
    const result = await getGrantInteraction(interactionId);
    switch (result.type) {
      case 'ready': {
        const session = await getSession();
        if (session.type === 'unauthenticated') {
          // Session died between the two calls: resume via the orchestrator.
          return { type: 'navigate', url: safeRedirect };
        }
        return { type: 'ready', email: session.user.user.email, interaction: result.interaction };
      }
      case 'sessionExpired':
        return { type: 'navigate', url: safeRedirect };
      case 'invalid':
        return { type: 'invalid' };
      case 'error':
        Logger.error('Get grant interaction failed', { message: result.message });
        return { type: 'invalid' };
      default:
        result satisfies never;
        return { type: 'invalid' };
    }
  };

  useEffect(() => {
    if (!interactionId || !safeRedirect) {
      setLoadState({ type: 'invalid' });
      return;
    }

    let cancelled = false;
    void resolveLoadState().then((next) => {
      if (cancelled) {
        return;
      }
      if (next.type === 'navigate') {
        window.location.assign(next.url);
      } else {
        setLoadState(next);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [interactionId]);

  useEffect(() => {
    if (loadState.type === 'ready') {
      track({ eventName: 'Connection-View', properties: { PageName: PAGE_NAME, Flow: FLOW, Intent: 'consent', EntryPoint: 'sign-in' } });
    }
  }, [loadState.type, track]);

  const trackClick = (cta: 'allow-access' | 'cancel' | 'sign-out') => {
    track({
      eventName: 'Connection-Click',
      properties: { PageName: PAGE_NAME, Flow: FLOW, Intent: 'consent', EntryPoint: 'sign-in', Cta: cta },
    });
  };

  const trackStatus = (statusType: 'success' | 'failure') => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: PAGE_NAME,
        Flow: FLOW,
        Intent: 'consent',
        EntryPoint: 'sign-in',
        Cta: 'allow-access',
        StatusType: statusType,
      },
    });
  };

  const handleApprove = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setShowError(false);
    trackClick('allow-access');

    const result = await grantScopes({ interactionId });

    switch (result.type) {
      case 'success':
        trackStatus('success');
        window.location.assign(safeRedirect);
        return;
      case 'sessionExpired':
      case 'invalidInteraction': {
        // Expired/used while the screen was open: resume the authorize flow,
        // which mints a fresh interaction (or issues a code if already granted).
        window.location.assign(safeRedirect);
        return;
      }
      case 'error':
        Logger.error('Grant scopes failed', { message: result.message });
        trackStatus('failure');
        setShowError(true);
        setIsSubmitting(false);
        return;
      default:
        result satisfies never;
    }
  };

  // Per product (Slack): keep a "Not you? Sign out" escape hatch for users
  // signed into the wrong account, since Cancel only returns to the client.
  // After the revoke, the orchestrator sees no session and routes to login.
  const handleSignOut = async () => {
    trackClick('sign-out');
    const result = await logout();
    if (result.type !== 'success') {
      // Revocation failed: the session is still live, so resuming authorize
      // would silently continue as the account the user tried to leave.
      Logger.error('Sign-out revoke failed', { message: result.message });
      setShowError(true);
      return;
    }
    window.location.assign(safeRedirect);
  };

  const handleDeny = () => {
    trackClick('cancel');
    // Backend forwards the error to the client's redirect_uri. Set it
    // structurally so it survives any redirect shape.
    const url = new URL(safeRedirect, window.location.origin);
    url.searchParams.set('error', 'access_denied');
    window.location.assign(`${url.pathname}${url.search}`);
  };

  if (loadState.type === 'invalid') {
    return (
      <Container
        data-testid="grant-page"
        backgroundColor="light"
        height="full"
        paddingX={isMobile ? 's' : 'xl'}
        paddingY={isMobile ? 's' : 'l'}
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: '100vh' }}
      >
        <Container width="full" maxWidth="480px" data-testid="grant-invalid-link">
          <SectionBannerRoot variant="critical">
            <SectionBannerIcon />
            <SectionBannerContent>
              <SectionBannerTitle>This link is invalid or has expired</SectionBannerTitle>
              <SectionBannerDescription>Return to the app you were connecting and start the authorization again.</SectionBannerDescription>
            </SectionBannerContent>
          </SectionBannerRoot>
        </Container>
      </Container>
    );
  }

  if (loadState.type === 'loading') {
    return (
      <Container data-testid="grant-page" height="full" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
        <Loader />
      </Container>
    );
  }

  const { interaction } = loadState;

  return (
    <Container
      data-testid="grant-page"
      backgroundColor="light"
      height="full"
      paddingX={isMobile ? 's' : 'l'}
      paddingY="xxl"
      alignItems="center"
      justifyContent="flex-start"
      style={{ minHeight: '100vh' }}
    >
      <Container overflow="visible" width="full" alignItems="center" justifyContent="center">
        <Container overflow="visible" width="full" maxWidth="480px">
          <Group variant="vertical" spacing="m" alignItems="stretch">
            <Group variant="horizontal" spacing="xs" alignItems="center" justifyContent="center" data-testid="grant-brand">
              {interaction.logoUrl ? (
                <img src={interaction.logoUrl} alt={interaction.displayName} width={54} height={54} />
              ) : (
                <Icon type="link" size="large" aria-hidden />
              )}
              <ConnectorLine />
              <Text as="span" color="semantic.text.secondary" aria-hidden>
                <Icon type="link-circle-outline" size="large" color="inherit" />
              </Text>
              <ConnectorLine />
              <BrandSymbol type="melio-secondary" size="medium" width={53} height={57} aria-label="Melio" />
            </Group>

            <Container textAlign="center">
              <Text textStyle="body2" data-testid="grant-page-title">
                <Text as="span" textStyle="body2Semi">
                  {interaction.displayName}
                </Text>{' '}
                wants access to your Melio account
              </Text>
            </Container>

            <Container border="regular" backgroundColor="white" paddingX="l" paddingY="l" style={{ borderRadius: '16px' }}>
              <Group variant="vertical" spacing="m">
                <Group variant="horizontal" spacing="xs" alignItems="center">
                  <Icon type="shield-check" size="large" aria-hidden />
                  <Text textStyle="body3Semi" data-testid="grant-access-title">
                    This will authorize the app to
                  </Text>
                </Group>

                <ScopeList scopes={interaction.missingScopes} testId="grant-missing-scopes" />

                <Group variant="vertical" spacing="xs" alignItems="flex-start" width="full">
                  <SectionBannerRoot variant="brand" data-testid="grant-signed-in-banner">
                    {/* The design shows a neutral info mark, not the brand variant's built-in
                        "?" status icon; centered vertically against a wrapping email. */}
                    <Container width="fit-content" style={{ alignSelf: 'center' }} aria-hidden>
                      {/* inherit: Icon's own brand color prop doesn't render in this app */}
                      <Text as="span" color="semantic.text.brand.rest">
                        <Icon type="info-fill" size="small" color="inherit" />
                      </Text>
                    </Container>
                    <SectionBannerContent>
                      <Text textStyle="body4">
                        Signed in as{' '}
                        <Text as="span" textStyle="body4Semi" data-testid="grant-signed-in-as">
                          {loadState.email}
                        </Text>
                      </Text>
                    </SectionBannerContent>
                  </SectionBannerRoot>
                  <Group variant="horizontal" spacing="xxs" alignItems="center">
                    <Text textStyle="body3" color="semantic.text.secondary">
                      Not you?
                    </Text>
                    {/* inline variant: penny's standalone Link only renders 16px,
                        the design's link is body3-sized (14px) */}
                    <Text textStyle="body3">
                      <Link
                        href="#"
                        variant="inline"
                        color="secondary"
                        label="switch account"
                        data-testid="grant-sign-out-link"
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          void handleSignOut();
                        }}
                      />
                    </Text>
                  </Group>
                </Group>

                {interaction.grantedScopes.length > 0 && (
                  <>
                    <Divider />
                    <Group variant="vertical" spacing="m" data-testid="grant-already-granted">
                      <Text textStyle="body3Semi">This app already has permissions to</Text>
                      <ScopeList scopes={interaction.grantedScopes} testId="grant-granted-scopes" />
                    </Group>
                  </>
                )}

                {showError && (
                  <SectionBannerRoot variant="critical" data-testid="grant-error-banner">
                    <SectionBannerIcon />
                    <SectionBannerContent>
                      <SectionBannerDescription>Something went wrong. Please try again.</SectionBannerDescription>
                    </SectionBannerContent>
                    <SectionBannerCloseButton onClick={() => setShowError(false)} />
                  </SectionBannerRoot>
                )}

                <Group variant="horizontal" spacing="xs" alignItems="center">
                  <Button
                    label="Cancel"
                    variant="tertiary"
                    size="medium"
                    isFullWidth
                    onClick={() => handleDeny()}
                    data-testid="grant-cancel-button"
                  />
                  <Button
                    label="Authorize"
                    variant="primary"
                    size="medium"
                    isFullWidth
                    isLoading={isSubmitting}
                    onClick={() => void handleApprove()}
                    data-testid="grant-allow-button"
                  />
                </Group>
              </Group>
            </Container>
          </Group>
        </Container>
      </Container>
    </Container>
  );
};
